import { Controller } from "@hotwired/stimulus";
import Combobox from "@github/combobox-nav";
import { api } from "./api";

export default class extends Controller {
  static targets = ["input", "container", "itemList", "item"];

  static values = {
    searchEndpoint: String,
    createEndpoint: String,
    entityType: String,
    allowCreate: Boolean,
    autocapitalize: Boolean,
    isSearching: Boolean,
    selectedItemsSelector: String,
  };

  connect() {
    this.setupCombobox();
    this.bindEvents();
    this.isKeyboardNavActive = false;
    this.itemListTarget.hidden = true;
  }

  get selectedItems() {
    let items = [
      ...document.querySelectorAll(this.selectedItemsSelectorValue),
    ].map((item) => ({
      id: Number(item.dataset.itemId),
      label: item.dataset.itemName,
    }));
    return items;
  }

  setupCombobox() {
    this.combobox = new Combobox(this.inputTarget, this.itemListTarget, {
      firstOptionSelectionMode: "selected",
    });

    // Setup ARIA attributes
    this.inputTarget.setAttribute("role", "combobox");
    this.inputTarget.setAttribute("aria-controls", this.itemListTarget.id);
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.setAttribute("aria-autocomplete", "list");
    this.itemListTarget.setAttribute("role", "listbox");
  }

  disconnect() {
    this.combobox.destroy();
  }

  bindEvents() {
    this.inputTarget.addEventListener("input", this.handleInput.bind(this));
    this.inputTarget.addEventListener("focus", () => {
      this.inputTarget.setAttribute("aria-expanded", "true");
      this.combobox.start();
    });
    this.inputTarget.addEventListener("blur", () => {
      this.inputTarget.setAttribute("aria-expanded", "false");
      setTimeout(() => {
        if (this.inputTarget.value.length === 0) {
          this.combobox.clearSelection();
          this.results = [];
          this.render(this.results);
        }
        this.itemListTarget.hidden = true;
        this.combobox.stop();
      }, 100);
    });
    this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this));

    this.itemListTarget.addEventListener(
      "mouseover",
      this.handleMouseOver.bind(this),
    );

    // some tom foolery so we can provide seemless mouse and keyboard nav
    this.itemListTarget.addEventListener("mousemove", () => {
      if (this.keyboardNavTimeout) {
        clearTimeout(this.keyboardNavTimeout);
        this.keyboardNavTimeout = null;
        this.isKeyboardNavActive = false;
      }
    });
  }

  handleMouseOver(event) {
    if (this.isKeyboardNavActive) return;
    // Only handle mouseover for option items that aren't disabled
    const option = event.target.closest(
      '[role="option"]:not([data-disabled="true"])',
    );
    if (!option) return;

    this.combobox.clearSelection();

    // Set the hovered item as selected
    option.setAttribute("aria-selected", "true");
    this.inputTarget.setAttribute("aria-activedescendant", option.id);

    // Fire the select event to maintain consistency with keyboard navigation
    option.dispatchEvent(new Event("combobox-select", { bubbles: true }));

    // Optionally scroll the item into view
    option.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  handleInput(event) {
    let query = event.currentTarget.value;

    // Auto-capitalize input if enabled
    if (this.autocapitalizeValue) {
      query = this.titleCase(query);
      this.inputTarget.value = query;

      // Preserve cursor position
      const cursorPosition = this.inputTarget.selectionStart;
      this.inputTarget.setSelectionRange(cursorPosition, cursorPosition);
    }

    this.performSearch(query);
  }

  handleKeydown(event) {
    if (this.inputTarget.value.length > 0) {
      this.itemListTarget.hidden = false;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      this.isKeyboardNavActive = true;

      // Set a timeout to allow keyboard navigation to take precedence
      if (this.keyboardNavTimeout) {
        clearTimeout(this.keyboardNavTimeout);
      }

      this.keyboardNavTimeout = setTimeout(() => {
        this.isKeyboardNavActive = false;
        this.keyboardNavTimeout = null;
      }, 2000);
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selected = this.itemListTarget.querySelector(
        '[aria-selected="true"]',
      );
      if (selected) {
        if (selected.dataset.itemId === "0") {
          this.handleCreate({ params: this.inputTarget.value });
        } else {
          this.handleItemSelect({ currentTarget: selected });
        }
      } else if (
        this.results.length === 0 &&
        this.allowCreateValue &&
        this.inputTarget.value
      ) {
        this.handleCreate({ params: this.inputTarget.value });
      }
      return;
    }

    if (event.key === "Escape") {
      this.itemListTarget.hidden = true;
      this.combobox.stop();
      this.combobox.clearSelection();
    }
  }

  async performSearch(query) {
    if (!query.length) {
      this.itemListTarget.hidden = true;
      this.combobox.stop();
      return;
    }

    this.isSearchingValue = true;
    this.render(this.results);

    try {
      const data = await api.get(
        `${this.searchEndpointValue}?q=${encodeURIComponent(query)}&entity=${this.entityTypeValue}`,
      );
      this.results = data.results.map((item) => ({
        id: item.id,
        label: item.text || item.name,
      }));

      this.isSearchingValue = false;
      this.render(this.results);

      // Start combobox navigation after results are rendered
      if (this.results.length > 0 || this.allowCreateValue) {
        this.combobox.start();
      }
    } catch (error) {
      console.error(error);
      this.isSearchingValue = false;
      this.render([]);
      this.combobox.stop();
    }
  }

  render(items = []) {
    const showCreateOption =
      this.allowCreateValue &&
      this.inputTarget.value &&
      !this.isSearchingValue &&
      items.length === 0;

    const dropdownHtml = `
      ${
        this.isSearchingValue && items.length === 0
          ? `<li class="py-2 px-3 text-sm text-center">
            <div class="loading loading-spinner loading-sm"></div>
           </li>`
          : showCreateOption
            ? `<li
              id="searchable-list-create-${this.entityTypeValue}"
              data-item-id="0"
              role="option"
              class="bg-primary text-primary-content option cursor-pointer flex flex-col items-start py-2 px-3 text-sm underline"
              data-action="mousedown->searchable-list#handleCreate"
            >
              Create "${this.inputTarget.value}"
            </li>`
            : items
                .map((item) => {
                  const isSelected = this.selectedItems.some(
                    (selectedItem) => selectedItem.id === item.id,
                  );
                  return `<li
                id="${item.id}"
                data-item-id="${item.id}"
                role="option"
                data-disabled="${isSelected}"
                class="option py-2 px-3 text-sm
                ${isSelected ? "bg-gray-500 text-gray-400 input-disabled cursor-not-allowed" : "cursor-pointer"}
                ${!isSelected ? 'aria-selected:bg-primary aria-selected:text-primary-content"' : ""}"
                data-action="mousedown->searchable-list#handleItemSelect click->searchable-list#handleItemSelect"
              >
                ${item.label} ${isSelected ? "(selected)" : ""}
              </li>
          `;
                })
                .join("")
      }
    `;

    this.itemListTarget.innerHTML = dropdownHtml;
    this.itemListTarget.hidden = false;
  }

  addItem(content) {
    if (!content || this.selectedItems.find((item) => item.id === content.id))
      return;

    try {
      const formData = new FormData();
      formData.append("entity_id", content.id);
      formData.append("entity", this.entityTypeValue.toLowerCase());

      fetch(`/admin/videos/add-to-relations-list`, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
      }).then((response) => {
        if (!response.ok) {
          console.error(response);
          alert("Error creating item");
        } else {
          response.text().then((html) => Turbo.renderStreamMessage(html));
        }
      });

      this.inputTarget.value = "";
      this.itemListTarget.hidden = true;
    } catch (error) {
      console.error("Error creating item:", error);
    }

    // Clear and stop combobox after selection
    this.combobox.clearSelection();
    this.combobox.stop();
  }

  handleItemSelect(event) {
    event.preventDefault?.();
    const itemId = event.currentTarget.dataset.itemId;
    if (!itemId) return;

    // Get the item from the current dropdown list
    const selectedElement = this.itemListTarget.querySelector(
      `[data-item-id="${itemId}"]`,
    );
    if (!selectedElement) return;

    if (this.selectedItems.find((item) => item.id === parseInt(itemId))) {
      return;
    }

    const item = {
      id: parseInt(itemId),
      label: selectedElement.textContent.trim(),
    };

    this.addItem(item);
    this.inputTarget.value = "";
    this.itemListTarget.hidden = true;
    this.combobox.stop();
  }

  async handleCreate(event) {
    const value = event.params?.value || this.inputTarget.value;

    if (!value) return;

    try {
      const formData = new FormData();
      let name = this.autocapitalizeValue ? this.titleCase(value) : value;

      if (this.entityTypeValue.toLowerCase().includes("person")) {
        formData.append("person[name]", name);
        formData.append("entity", this.entityTypeValue.toLowerCase());
      } else {
        formData.append(`${this.entityTypeValue.toLowerCase()}[name]`, name);
      }

      formData.append("context", "stream");

      fetch(this.createEndpointValue, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
      }).then((response) => {
        if (!response.ok) {
          console.error(response);
          alert("Error creating item");
        } else {
          response.text().then((html) => Turbo.renderStreamMessage(html));
        }
      });

      this.inputTarget.value = "";
      this.itemListTarget.hidden = true;
    } catch (error) {
      console.error("Error creating item:", error);
    }
  }

  titleCase(str) {
    return str.replace(/\w\S*/g, (text) => {
      return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase();
    });
  }

  toggleAutoCapitalize() {
    this.autocapitalizeValue = !this.autocapitalizeValue;
  }
}
